import {
  createContext,
  Fragment,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { errorConfiguration } from '../ErrorConfig';
import { ErrorView } from '../ErrorView';
import { type AppError, FatalError } from '../errors';
import { ErrorType } from '../types';

export const ErrorContext = createContext<{
  error: AppError | undefined;
  emitError: (error: AppError) => void;
  clearErrorNotifications: () => void;
  successMessage: string | undefined;
  emitSuccess: (message: string) => void;
  clearSuccessNotification: () => void;
} | null>(null);

export const useErrorContext = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Context provider for errors
export const ErrorHandler = ({ children }: PropsWithChildren) => {
  const [error, setError] = useState<AppError>();
  const [successMessage, setSuccessMessage] = useState<string>();

  const clearErrorNotifications = useCallback(() => {
    setError(undefined);
  }, []);

  const clearSuccessNotification = useCallback(() => {
    setSuccessMessage(undefined);
  }, []);

  const errorView = useMemo(() => {
    if (error instanceof FatalError) {
      return <ErrorView />;
    }
    const errorCode = error?.code || error?.details?.code;
    if (!errorCode) {
      return undefined;
    }
    const errorConfig =
      errorConfiguration && errorCode in errorConfiguration
        ? errorConfiguration[errorCode]
        : undefined;
    if (errorConfig && errorConfig.errorType === ErrorType.FULL_PAGE) {
      return errorConfig.errorView ? (errorConfig.errorView as ReactNode) : <ErrorView />;
    }
    return undefined;
  }, [error]);

  const value = useMemo(
    () => ({
      error,
      emitError: setError,
      clearErrorNotifications,
      successMessage,
      emitSuccess: setSuccessMessage,
      clearSuccessNotification,
    }),
    [error, clearErrorNotifications, successMessage, clearSuccessNotification],
  );

  if (errorView) {
    return <Fragment key={'error-view'}>{errorView}</Fragment>;
  }

  return (
    <ErrorContext.Provider value={value}>
      <Fragment key={'initial-error-render'}>{children}</Fragment>
    </ErrorContext.Provider>
  );
};
